import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChoiceList,
  EmptyState,
  ErrorNotice,
  Field,
  LoadingState,
  StripePayment,
  TextInput,
  type ChoiceOption,
  type StripePaymentOutcome,
  type StripePaymentQuote,
} from '@bcecloud/portal-ui';
import { LinkIcon } from 'lucide-react';
import {
  PAY_LINK_ERROR,
  PAY_LINK_KIND,
  PayLinkRequestError,
  confirmPayLinkPayment,
  payPayLink,
  quotePayLinkPayment,
  readPayLink,
  type PayLinkChoice,
  type PayLinkView,
} from '@/lib/pay-link';
import {
  BCE_PAGE_BRAND,
  CHOICE,
  Checkout,
  EMAIL,
  MIN_PAYMENT,
  PaidInFull,
  ProcessingNotice,
  Receipt,
  SummaryRow,
  longDate,
  money,
  parseAmount,
} from '@/components/PayCheckout';
import { SeriesPay } from './SeriesPay';

/**
 * The no-login payment page (`/pay/<token>`): whoever holds the link — often
 * the client's accounts-payable team, with no portal account — sees what's
 * owed and pays by bank (ACH) or card. Lives outside the signed-in shell because
 * the link, not a sign-in, names the event.
 * A series contract's link opens {@link SeriesPay}: its events, paid together.
 */

/** The options: each outstanding line, everything owed, or another amount. */
function payOptions(view: PayLinkView): ChoiceOption<string>[] {
  const outstanding = view.schedule.lines.filter((line) => line.outstanding > 0);
  const options: ChoiceOption<string>[] = outstanding.map((line) => {
    const due = line.dueDate ? `Due ${longDate(line.dueDate)}` : line.dueNote ? `Due ${line.dueNote.toLowerCase()}` : undefined;
    return { key: line.key, label: line.label, ...(due ? { detail: due } : {}), value: money(line.outstanding) };
  });
  // "Pay in full" only adds something when more than one line is still open.
  if (outstanding.length > 1) {
    options.push({ key: CHOICE.total, label: 'Pay the full balance', detail: 'Everything still owed, in one payment', value: money(view.schedule.amountDue) });
  }
  options.push({ key: CHOICE.custom, label: 'Another amount', detail: 'More than the deposit, or part of the balance' });
  return options;
}

/** The chosen option as what the API charges, or why it can't be charged yet. */
function resolveChoice(view: PayLinkView, key: string, customAmount: string): PayLinkChoice | string {
  if (key === CHOICE.total) return { line: CHOICE.total, amount: view.schedule.amountDue };
  if (key === CHOICE.custom) {
    const amount = parseAmount(customAmount);
    if (amount === null || amount < MIN_PAYMENT) return `Enter an amount of at least ${money(MIN_PAYMENT)}.`;
    if (amount > view.schedule.amountDue) return `That's more than the ${money(view.schedule.amountDue)} still owed.`;
    return { line: CHOICE.custom, amount };
  }
  const line = view.schedule.lines.find((candidate) => candidate.key === key);
  return line ? { line: line.key, amount: line.outstanding } : 'Choose what to pay.';
}

/** The royal panel: who's being paid, how much, and for what — and a card fee once one is quoted. */
function Summary({
  view,
  amount,
  feeQuote = null,
}: {
  readonly view: PayLinkView;
  readonly amount: number | null;
  readonly feeQuote?: StripePaymentQuote | null;
}): React.ReactElement {
  const eventDate = longDate(view.eventDate);
  return (
    <>
      <p className="text-portal-small text-white/80">Pay Best Corporate Events</p>
      <p className="mt-1 text-[40px] leading-tight font-bold tracking-[-1px]">{money(feeQuote?.total ?? amount ?? view.schedule.amountDue)}</p>
      {feeQuote ? (
        <p className="text-portal-small text-white/80">
          {money(feeQuote.amount)} + {money(feeQuote.fee)} credit card fee ({feeQuote.feePercent}%)
        </p>
      ) : null}
      <p className="mt-3 text-portal-small text-white/80">
        {[view.accountName, view.programName, eventDate].filter(Boolean).join(' · ')}
      </p>
      {view.contractNumber ? <p className="text-portal-micro text-white/65">Contract #{view.contractNumber}</p> : null}
      <dl className="mt-8 divide-y divide-white/20 border-y border-white/20">
        <SummaryRow label="Contract total" value={money(view.schedule.contractedAmount)} />
        <SummaryRow label="Paid so far" value={money(view.schedule.amountPaid)} />
        <SummaryRow label="Balance due" value={money(view.schedule.amountDue)} strong />
      </dl>
    </>
  );
}

/** One event's balance: pick a line, the full balance, or another amount. */
function EventPay({ token, view }: { readonly token: string; readonly view: PayLinkView }): React.ReactElement {
  const queryClient = useQueryClient();
  const [choiceKey, setChoiceKey] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<{ outcome: StripePaymentOutcome; amount: number; email: string } | null>(null);
  const [feeQuote, setFeeQuote] = useState<StripePaymentQuote | null>(null);

  const options = useMemo(() => payOptions(view), [view]);
  const selected = choiceKey ?? options[0]?.key ?? CHOICE.custom;
  const choice = resolveChoice(view, selected, customAmount);
  const chosen = typeof choice === 'string' ? null : choice;

  if (done) {
    return (
      <Checkout summary={<Summary view={view} amount={done.amount} />}>
        <Receipt outcome={done.outcome} amount={done.amount} email={done.email} />
      </Checkout>
    );
  }

  if (view.paidInFull) {
    return (
      <Checkout summary={<Summary view={view} amount={0} />}>
        <PaidInFull message="Nothing is owed on this event. Thank you." />
      </Checkout>
    );
  }

  /** Everything on the page that must be right before Stripe charges. */
  function beforePay(): string | null {
    if (typeof choice === 'string') return choice;
    return EMAIL.test(email.trim()) ? null : 'Enter the email address the receipt should go to.';
  }

  return (
    <Checkout summary={<Summary view={view} amount={chosen?.amount ?? null} feeQuote={feeQuote} />}>
      <ProcessingNotice since={view.processingSince} />

      <h2 className="mb-2 text-portal-small font-bold text-portal-ink">Amount</h2>
      <ChoiceList
        label="What to pay"
        options={options}
        value={selected}
        onChange={setChoiceKey}
        renderSelected={(key) =>
          key === CHOICE.custom ? (
            <Field label="Amount" htmlFor="pay-amount" hint={typeof choice === 'string' && customAmount ? choice : `Up to ${money(view.schedule.amountDue)}.`}>
              <TextInput
                id="pay-amount"
                inputMode="decimal"
                placeholder="0.00"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
            </Field>
          ) : null
        }
      />

      <div className="mt-6">
        <Field label="Email for your receipt" htmlFor="pay-email" required>
          <TextInput id="pay-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
      </div>

      <h2 className="mt-6 mb-2 text-portal-small font-bold text-portal-ink">Payment method</h2>
      {view.publishableKey ? (
        <StripePayment
          publishableKey={view.publishableKey}
          amountCents={chosen ? Math.round(chosen.amount * 100) : null}
          amountLabel={chosen ? money(chosen.amount) : ''}
          cardSurchargePercent={view.cardSurchargePercent}
          formatMoney={money}
          onFeeQuote={setFeeQuote}
          beforePay={beforePay}
          quote={(confirmationTokenId) => {
            if (!chosen) throw new Error('Choose what to pay.');
            return quotePayLinkPayment(token, chosen, confirmationTokenId);
          }}
          pay={(confirmationTokenId, expectedTotalCents) => {
            if (!chosen) throw new Error('Choose what to pay.');
            return payPayLink(token, chosen, confirmationTokenId, expectedTotalCents, email.trim());
          }}
          confirmPayment={(paymentIntentId) => confirmPayLinkPayment(token, paymentIntentId)}
          onComplete={(outcome) => {
            if (chosen) setDone({ outcome, amount: chosen.amount, email: email.trim() });
            void queryClient.invalidateQueries({ queryKey: ['pay-link', token] });
          }}
        />
      ) : (
        <ErrorNotice title="Online payment is unavailable" message="Please contact Best Corporate Events to pay this balance." />
      )}
    </Checkout>
  );
}

/**
 * The payment page.
 *
 * @returns The page.
 */
export function PayPage(): React.ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const payLink = useQuery({
    queryKey: ['pay-link', token],
    queryFn: ({ signal }) => readPayLink(token, signal),
    retry: false,
  });

  if (payLink.isPending) {
    return (
      <main style={BCE_PAGE_BRAND} className="grid min-h-screen place-items-center bg-white">
        <LoadingState label="Loading your balance…" />
      </main>
    );
  }

  if (payLink.isError) {
    const inactive = payLink.error instanceof PayLinkRequestError && payLink.error.kind === PAY_LINK_ERROR.inactive;
    return (
      <Checkout summary={<p className="text-[28px] leading-tight font-bold">Pay Best Corporate Events</p>}>
        {inactive ? (
          <EmptyState
            icon={<LinkIcon />}
            title="This payment link is no longer active"
            message="It may have been replaced by a newer link. Ask Best Corporate Events to send you a new one."
          />
        ) : (
          <ErrorNotice title="Payment page unavailable" message={payLink.error.message} />
        )}
      </Checkout>
    );
  }

  return payLink.data.kind === PAY_LINK_KIND.series ? (
    <SeriesPay token={token} view={payLink.data} />
  ) : (
    <EventPay token={token} view={payLink.data} />
  );
}
